import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { HandCoins } from "@fleet/icons";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@fleet/ui";
import { api, createApiQuery } from "@/lib/api";
import { useSession } from "@/lib/auth";
import { billingQueryKeys, type components, formatCents } from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type AuthorEarnings = components["schemas"]["AuthorEarningsResponse"];

/**
 * AUTHOR earnings from revenue share (monetization phase 2).
 *
 * The server computes the split (engagement measured server-side + largest
 * remainder) and here it is ONLY displayed: entries per period with their
 * status ('draft' = estimate still recomputable; 'settled' = consolidated)
 * and totals per currency. If the author has no payout, the card does not
 * appear (zero noise for learners and authors without a share).
 */
export function AuthorEarnings() {
  const { t, locale } = useI18n();
  const session = useSession();

  const earningsQuery = createApiQuery<AuthorEarnings, ErrorModel>(
    () => billingQueryKeys.earnings(),
    async () => api.GET("/api/billing/earnings", {}),
    () => ({ staleTime: 30_000, enabled: !session.isVisitor() }),
  );

  const entries = createMemo(() => earningsQuery.data?.entries ?? []);
  const totals = createMemo(() => earningsQuery.data?.totals ?? []);

  const dateRange = (startIso: string, endIso: string): string => {
    const fmt = new Intl.DateTimeFormat(locale(), { dateStyle: "medium" });
    return `${fmt.format(new Date(startIso))} – ${fmt.format(new Date(endIso))}`;
  };

  return (
    <>
      {/* P23/I3 · el bloque entero se oculta cuando no hay entradas, así que con
          el API caído el autor no ve NADA y no puede distinguirlo de «este mes
          no has ganado». La caja de error solo aparece si la query falló de
          verdad (para un invitado la query está deshabilitada: no hay error). */}
      <Show when={earningsQuery.isError}>
        <QueryErrorState
          testId="author-earnings-error"
          compact
          onRetry={() => void earningsQuery.refetch()}
        />
      </Show>
      <Show when={entries().length > 0}>
        <Card data-testid="author-earnings">
          <CardHeader>
            <CardTitle class="flex items-center gap-2">
              <HandCoins class="h-5 w-5 text-primary" />
              {t(msg`Author earnings`)}
            </CardTitle>
            <p class="text-xs text-muted-foreground">
              {t(
                msg`Your share of the revenue pool, based on lessons completed by your learners. Payouts are handled manually by the academy.`,
              )}
            </p>
          </CardHeader>
          <CardContent class="space-y-4">
            <div class="flex flex-wrap gap-3">
              <For each={totals()}>
                {(total) => (
                  <div
                    class="rounded-xl border p-3 min-w-36"
                    data-testid={`earnings-total-${total.currency}`}
                  >
                    <div class="text-xs text-muted-foreground">{total.currency}</div>
                    <div
                      class="text-xl font-bold"
                      data-testid={`earnings-settled-${total.currency}`}
                    >
                      {formatCents(total.settledCents, total.currency, locale())}
                    </div>
                    <Show when={total.draftCents > 0}>
                      <div class="text-xs text-muted-foreground">
                        {t(msg`Pending`)}:{" "}
                        <span data-testid={`earnings-draft-${total.currency}`}>
                          {formatCents(total.draftCents, total.currency, locale())}
                        </span>
                      </div>
                    </Show>
                  </div>
                )}
              </For>
            </div>

            <div class="space-y-2">
              <For each={entries()}>
                {(entry) => (
                  <div
                    class="flex items-center justify-between gap-3 text-sm"
                    data-testid={`earnings-entry-${entry.periodId}`}
                  >
                    <div class="min-w-0">
                      <div class="font-medium truncate">
                        {dateRange(entry.periodStart, entry.periodEnd)}
                      </div>
                      <div class="text-xs text-muted-foreground">
                        {t(msg`Completed lessons by your learners`)}: {entry.engagementUnits}
                      </div>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                      <span class="font-semibold">
                        {formatCents(entry.shareCents, entry.currency, locale())}
                      </span>
                      <Badge variant={entry.status === "settled" ? "default" : "secondary"}>
                        {entry.status === "settled" ? t(msg`Settled`) : t(msg`Pending`)}
                      </Badge>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </CardContent>
        </Card>
      </Show>
    </>
  );
}
